import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  initialBrandExclusionsViewModel,
  loadCampaign,
  loadBundleCountries,
  saveCampaignAndLinkedData,
  modelToViewModelForCampaign,
  loadConversionGoalDetails,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  readLocationCriterions,
  readCampaignNegativeWebpages,
  readDayTimeCriterions,
  readNewCustomerAcquisitionGoals
} from '@bingads-webui-campaign-react/perf-max-data';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { usePreference, preferenceNames } from '@bingads-webui-campaign-react/pmax-lite-data';
import { MAX_CONVERSIONS } from '@bingads-webui-campaign-react/bid-strategy-component';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { LogTargetCpaCheckOnSave } from '@bingads-webui-campaign-react/target-cpa-warning-banner';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation/index';
import { viewModelToBulkModelForDisclaimerAssociation } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import { useLocalizeBudget } from '../budget/utils/use-budget-localized';

export const roasMtToUI = roas => (_.isNumber(roas) ? roas * 100 : null);
export const roasUiToMt = roas => (_.isNumber(roas) ? (roas / 100).toFixed(4) : null);

export const useCampaignSettingStepRepository = () => {
  const { localizeBudget } = useLocalizeBudget();
  const [{ value: currencyPreference }] = usePreference(preferenceNames.SelectedCurrency);
  const { state, send } = useWizardService();
  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid, campaignId } = scope.values;

  const modelToViewModel = useCallback((campaign) => {
    const budgetAmount = _.get(campaign, ['Budget', 'Amount']);
    const bidStrategy = _.get(campaign, ['BiddingScheme', 'Type']);
    const cpa = _.get(campaign, ['BiddingScheme', 'TargetCpa']);
    const roas = parseFloat(_.get(campaign, ['BiddingScheme', 'TargetRoas'])) || null;
    const language = _.get(campaign, ['Languages', 0]);
    const isInitialAdditionalDetailsChecked = _.isNumber(cpa) || _.isNumber(roas);

    return {
      budgetAmount: localizeBudget(budgetAmount, 'USD'),
      bidStrategy,
      currency: currencyPreference,
      cpa,
      roas: roasMtToUI(roas),
      language,
      isInitialAdditionalDetailsChecked,
      isMarketingMetricValid: true,
    };
  }, [currencyPreference, localizeBudget]);

  const load = useCallback(() => {
    activity.trace('[PmaxNorthStarSettingWizard] - loadData started in campaign settings step');
    const savedBrandExclusions = state.context.wizardData.brandExclusions;
    const BrandExclusionsViewModel = savedBrandExclusions
      ? { data: savedBrandExclusions, isDirty: false, isValid: true }
      : initialBrandExclusionsViewModel;
    const { Permissions, DynamicPermissions } = serverContext || {};
    const permissions = { dynamic: { ...DynamicPermissions }, ...Permissions };
    const {
      NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
    } = permissions.dynamic;


    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId), // This is necessary to read ['Budget', 'CampaignConversionGoal/Goals'] from MT as the original model doesn't come with it
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
      campaignNegativeWebpages: readCampaignNegativeWebpages(cid, aid, campaignId),
      brandExclusions: Promise.resolve(BrandExclusionsViewModel),
      conversionGoalDetails: loadConversionGoalDetails({ cid, aid }),
      ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoals: readNewCustomerAcquisitionGoals(cid, aid) }),
    };

    return Promise.props(loadPromises).then(({
      campaign,
      locationTargets,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
      campaignNegativeWebpages,
      brandExclusions,
      newCustomerAcquisitionGoals,
      conversionGoalDetails,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: { ...campaign }, // MT sends UserPaused, if we send this back they mark it as active. So we just use Paused from the saved ad model
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        campaignNegativeWebpagesModel: campaignNegativeWebpages ? campaignNegativeWebpages.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        budgetAmount: campaign.Budget.Amount,
        ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoalsModel: newCustomerAcquisitionGoals ? newCustomerAcquisitionGoals.value : [] }),
      });

      activity.trace('[PmaxNorthStarSettingWizard] - loadData succeeded in campaign settings step');
      activity.signal('[PmaxNorthStaSettingrWizard] - loadData succeeded in campaign settings step');

      return {
        campaign: campaignData.data,
        brandExclusions: brandExclusions.data,
        conversionGoalDetails,
        ...modelToViewModel(campaign),
        ...bundleCountriesAndLocationCriterions,
      };
    }).catch((errorData) => {
      activity.trace(`[PmaxNorthStaSettingrWizard] loadData failed. Error: ${JSON.stringify(errorData)}`);
      setClarityTag({ IsPmaxNorthStarSettingFailure: 'true' });

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, campaignId, cid, modelToViewModel, serverContext, state.context.wizardData.brandExclusions]);

  const save = useCallback(({
    data,
    initialData,
  }) => {
    const {
      campaign: campaignToSave,
      bundleCountries,
      bundleLocationCriterions,
      brandExclusions: brandExclusionsToSave,
      budgetAmount,
      bidStrategy,
      cpa,
      roas,
      isBudgetValid,
      isMarketingMetricValid,
    } = data;

    const { Permissions, DynamicPermissions } = serverContext || {};
    const permissions = { dynamic: { ...DynamicPermissions }, ...Permissions };

    if (!isBudgetValid || !isMarketingMetricValid) {
      return Promise.reject();
    }

    const { campaign: initialCampaign } = initialData;

    const { locationTargets: savedLocationTargets } = campaignToSave;

    const newCampaign = {
      ...campaignToSave,
      BiddingScheme: {
        Type: bidStrategy,
        ...(
          bidStrategy === MAX_CONVERSIONS.type
            ? { TargetCpa: cpa }
            : { TargetRoas: roasUiToMt(roas) }
        ),
      },
      Budget: {
        Amount: i18n.parseDecimal(budgetAmount),
        Type: 'DailyStandard',
      },
    };

    const savePromises = {
      campaign: saveCampaignAndLinkedData({
        campaign: { data: newCampaign },
        originalCampaign: { data: initialCampaign },
        brandExclusions: { data: brandExclusionsToSave },
        bundleCountries,
        bundleLocationCriterions,
        viewModelToBulkModelForDisclaimerAssociation,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateCampaign'),
      }),
    };

    return Promise.props(savePromises).then(({
      campaign: campaignResults,
    }) => {
      activity.trace('[PmaxNorthStarSettingWizard] - campaign settings updated');
      activity.signal('[PmaxNorthStaSettingrWizard] - campaign settings updated');

      const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
        scenarioName: 'BudgetBidCampaignImportNotification',
        activityName: 'PmaxCampaignSettingPage',
      });

      showBudgetBidCampaignNotifications({
        campaignItemList: [initialCampaign],
        oldBudget: initialCampaign.Budget,
        newBudget: newCampaign.Budget,
        oldBidStrategy: initialCampaign.BiddingScheme,
        newBidStrategy: newCampaign.BiddingScheme,
        activity: BudgetBidCampaignImportNotificationActivity,
      });

      const { campaign, brandExclusions } = campaignResults;

      LogTargetCpaCheckOnSave(i18n, permissions, campaign?.data?.Budget?.Amount, campaign?.data?.BiddingScheme.TargetCpa, activity, 'PmaxNorthStarSettingWizard');

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: { ...campaign.data, locationTargets: savedLocationTargets }, brandExclusions: brandExclusions.data });

      return {
        campaign: { ...campaign.data, locationTargets: savedLocationTargets },
        brandExclusions: brandExclusions.data,
        bundleLocationCriterions,
        bundleCountries,
      };
    }).catch((errorData) => {
      activity.trace('[PmaxNorthStarSettingWizard] - campaign settings update failed');
      setClarityTag({ IsPmaxNorthStarSettingFailure: 'true' });

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, i18n, scenarioContext, send, serverContext]);

  return {
    load,
    save,
  };
};
